import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaDatabase, FaChartBar, FaServer, FaLock } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { theme } from '../../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary};
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Highlight = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(1, 191, 113, 0.1);
  }
`;

const CardIcon = styled.div`
  color: #01bf71;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CardText = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const FeaturesList = styled.div`
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FeatureIcon = styled.div`
  color: #01bf71;
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #fff;
`;

const ProcessSection = styled.div`
  margin-bottom: 80px;
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ProcessStep = styled.div`
  display: flex;
  gap: 20px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  background: #01bf71;
  color: #010606;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #01bf71, #018f55);
  border-radius: 10px;
  padding: 60px;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const CTATitle = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: transparent;
  border: 2px solid ${theme.colors.secondary};
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
`;

const DataDevelopment = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Data Development & Analytics Solutions | Afrotech Group</title>
        <meta name="description" content="Professional data development services including database design, data analytics, business intelligence, and POPIA-compliant data solutions. Unlock the power of your data with Afrotech Group." />
        <meta name="keywords" content="data development, database design, data analytics, business intelligence, POPIA compliance, data solutions" />
      </Helmet>
      
      <ContentWrapper>
        <PageHeader>
          <Title><Highlight>Data Development</Highlight> & Analytics Solutions</Title>
          <Subtitle>
            Unlock the power of your data with our comprehensive data development, management, and analytics services
          </Subtitle>
        </PageHeader>
        
        <Section>
          <SectionTitle>Our Data Development Services</SectionTitle>
          <Grid>
            <Card>
              <CardIcon><FaDatabase /></CardIcon>
              <CardTitle>Database Design & Development</CardTitle>
              <CardText>
                Custom database solutions designed for optimal performance, scalability, and data integrity.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Relational database design</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>NoSQL database implementation</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Data modeling and optimization</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaChartBar /></CardIcon>
              <CardTitle>Data Analytics & Visualization</CardTitle>
              <CardText>
                Transform your raw data into actionable insights with advanced analytics and intuitive visualizations.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Business intelligence dashboards</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Predictive analytics</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Interactive data reporting</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaServer /></CardIcon>
              <CardTitle>Data Integration & Migration</CardTitle>
              <CardText>
                Seamlessly integrate data from multiple sources or migrate to new systems without losing valuable information.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>ETL (Extract, Transform, Load) processes</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>API development for data exchange</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Legacy system data migration</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaLock /></CardIcon>
              <CardTitle>POPIA-Compliant Data Solutions</CardTitle>
              <CardText>
                Ensure your data management practices comply with South Africa's Protection of Personal Information Act (POPIA).
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Data privacy assessments</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Secure data storage solutions</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Compliant data processing systems</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <Section>
          <SectionTitle>Technologies We Use</SectionTitle>
          <Grid>
            <Card>
              <CardTitle>Database Technologies</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>PostgreSQL</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>MongoDB</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>MySQL & MariaDB</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Microsoft SQL Server</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>Data Processing</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Apache Spark</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Python (Pandas, NumPy)</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>R for statistical analysis</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>ETL tools</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>Visualization Tools</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Power BI</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Tableau</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>D3.js</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Custom dashboard solutions</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>Cloud Data Platforms</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>AWS (RDS, Redshift, S3)</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Azure (SQL Database, Cosmos DB)</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Google Cloud (BigQuery, Datastore)</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <ProcessSection>
          <SectionTitle>Our Data Development Process</SectionTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepTitle>Data Assessment & Strategy</StepTitle>
                <StepDescription>
                  We analyze your current data infrastructure, identify challenges and opportunities, and develop a comprehensive data strategy aligned with your business goals.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepTitle>Data Architecture Design</StepTitle>
                <StepDescription>
                  Our data architects design a robust, scalable, and secure data architecture that supports your current needs and future growth.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepTitle>Development & Implementation</StepTitle>
                <StepDescription>
                  We build and implement your data solution, including databases, ETL processes, analytics tools, and visualization dashboards.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepTitle>Data Integration & Migration</StepTitle>
                <StepDescription>
                  We seamlessly integrate your new data systems with existing platforms and migrate data with minimal disruption to your operations.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepTitle>Testing & Quality Assurance</StepTitle>
                <StepDescription>
                  Our team conducts thorough testing to ensure data accuracy, system performance, and security before final deployment.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>6</StepNumber>
              <StepContent>
                <StepTitle>Training & Support</StepTitle>
                <StepDescription>
                  We provide comprehensive training for your team and ongoing support to ensure you get the most value from your data systems.
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Unlock the Power of Your Data?</CTATitle>
          <CTAText>
            Contact our data development team today for a free consultation and discover how we can help transform your data into a strategic asset.
          </CTAText>
          <CTAButton to="/contact">Get Started</CTAButton>
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default DataDevelopment;
